/**
 * P23.1 · IntentFeedbackButton
 *
 * Botón "¿Acerté?" que aparece junto al badge "Solo chat" en mensajes
 * clasificados como categoría A. Al hacer clic, muestra dos opciones:
 *   - "Sí"  → POST feedback {was_correct: true}
 *   - "No"  → POST feedback {was_correct: false, correct_category: "B"}
 *
 * Después de responder, el botón desaparece y muestra un ✓ discreto.
 * Fire-and-forget: si el POST falla, silenciamos.
 */
import { useState } from 'react';
import axios from 'axios';
import { ThumbsUp, ThumbsDown, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  es: {
    prompt: '¿Acerté?',
    yes: 'Sí',
    no: 'No',
    thanks: '¡Gracias!',
  },
  en: {
    prompt: 'Was I right?',
    yes: 'Yes',
    no: 'No',
    thanks: 'Thanks!',
  },
};

export function IntentFeedbackButton({
  predictedCategory,
  originalMessage,
  conversationId = null,
  language = 'es',
}) {
  const t = COPY[language === 'en' ? 'en' : 'es'];
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (wasCorrect) => {
    if (sending || sent) return;
    setSending(true);
    try {
      const token = localStorage.getItem('damyxia_token');
      const body = {
        original_message: originalMessage || '(sin mensaje original)',
        predicted_category: predictedCategory,
        was_correct: wasCorrect,
        conversation_id: conversationId,
      };
      // Si el usuario dice que fallamos categoría A, la correcta era B.
      if (!wasCorrect) {
        body.correct_category = predictedCategory === 'A' ? 'B' : 'A';
      }
      await axios.post(`${API}/api/chat/intent-feedback`, body,
        token ? { headers: { Authorization: `Bearer ${token}` } } : {},
      );
      setSent(true);
    } catch {
      // Silent fail — feedback es fire-and-forget.
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] text-emerald-400/80"
        data-testid="intent-feedback-thanks"
      >
        <Check className="w-3 h-3" /> {t.thanks}
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] text-zinc-500"
      data-testid="intent-feedback-widget"
    >
      <span>{t.prompt}</span>
      <button
        type="button"
        onClick={() => submit(true)}
        disabled={sending}
        title={t.yes}
        className="p-0.5 rounded hover:bg-emerald-500/10 hover:text-emerald-400 transition-colors"
        data-testid="intent-feedback-yes"
      >
        <ThumbsUp className="w-3 h-3" />
      </button>
      <button
        type="button"
        onClick={() => submit(false)}
        disabled={sending}
        title={t.no}
        className="p-0.5 rounded hover:bg-rose-500/10 hover:text-rose-400 transition-colors"
        data-testid="intent-feedback-no"
      >
        <ThumbsDown className="w-3 h-3" />
      </button>
    </span>
  );
}

export default IntentFeedbackButton;
